import { plural } from './plural';

/**
 * Wording for the NFBPA Early Enrollment plan, Stripe-first route
 * (PEDS-17209).
 *
 * Early Enrollment is one payment for 6 months of access, for members and
 * retail learners alike. Stripe bills it every 6 months, and the gateway
 * webhook ends it with its first period. The staff-hub attach action stores
 * its 6-month price as monthly, so without this file the plan card reads
 * "$594 per month" and Settings says "Renews on". PlanCard.tsx, Plans/index.tsx
 * and subscriptionStatus.ts call it, and deleting it with those calls restores
 * the ordinary wording.
 */

/** The staff-hub slug of the Early Enrollment plan. */
export const TERM_PLAN_SLUG = 'all-access-early-enrollment';

/** The paid-plan button everywhere but on the Early Enrollment card. */
const SUBSCRIBE_LABEL = 'Subscribe to All Access';

export const isTermPlan = (slug: string | null | undefined): boolean =>
  slug === TERM_PLAN_SLUG;

interface TermPlanCardCopy {
  cta: string;
  per: string;
  coverLine: (programCount: number) => string;
  features: (programCount: number) => string[];
  note: string;
}

/**
 * What the plan card says for the Early Enrollment plan in place of its
 * interval wording, or null for any other plan. The features are the monthly
 * ones without "Cancel anytime", since there is nothing to cancel.
 */
export const termPlanCardCopy = (
  slug: string | null | undefined
): TermPlanCardCopy | null =>
  isTermPlan(slug)
    ? {
        cta: 'Get Early Enrollment',
        per: 'One payment',
        coverLine: () => '6 months of access. Does not renew.',
        features: (programCount) => [
          `Full access to all ${programCount} ${plural(
            programCount,
            'AI program',
            'AI programs'
          )}`,
          '15 modules of AI skills building',
          'Access to community',
          'Hands-on exercises & guided demos',
          'Earn skills badges and certificates of completion',
        ],
        note: "One payment for 6 months of access. You won't be charged again.",
      }
    : null;

/** The button label on a plan's card. */
export const planButtonLabel = (slug: string | null | undefined): string =>
  termPlanCardCopy(slug)?.cta ?? SUBSCRIBE_LABEL;

/**
 * The button labels the payment-terms line quotes, one per kind of card on the
 * page, so it still names the button above it when Early Enrollment is the
 * first paid card.
 */
export const paymentTermsButtonLabels = (
  slugs: Array<string | null | undefined>
): string =>
  [...new Set(slugs.map(planButtonLabel))]
    .map((label) => `"${label}"`)
    .join(' or ');

/** The Settings detail for an active Early Enrollment subscription. */
export const termSubscriptionDetail = (endDate: string): string =>
  `Access until ${endDate}. One payment, does not renew.`;
